import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  IconButton,
  Typography,
  TextField,
  CircularProgress,
} from "@mui/material";
import { Save as SaveIcon, Close as CancelIcon } from "@mui/icons-material";
import { apiClient } from "../../utils/apiClient";

interface AdminNotesCellProps {
  shipmentId: string;
  initialNotes?: string;
  onNotesUpdate?: (notes: string) => void;
}

export function AdminNotesCell({
  shipmentId,
  initialNotes = "",
  onNotesUpdate,
}: AdminNotesCellProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [notes, setNotes] = useState(initialNotes);
  const [editValue, setEditValue] = useState(initialNotes);
  const [saving, setSaving] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    setNotes(initialNotes);
    setEditValue(initialNotes);
  }, [initialNotes]);

  const handleEditStart = () => {
    setIsEditing(true);
    setEditValue(notes);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditValue(notes);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await apiClient.post("/shipments/update-notes", {
        shipmentId,
        adminNotes: editValue,
      });

      setNotes(editValue);
      setIsEditing(false);
      onNotesUpdate?.(editValue);
    } catch (error) {
      console.error("Failed to save notes:", error);
      // Revert on error
      setEditValue(notes);
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      handleCancel();
    } else if (e.key === "Enter" && e.ctrlKey) {
      handleSave();
    }
  };

  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isEditing]);

  if (isEditing) {
    return (
      <Box
        sx={{
          position: "relative",
          width: "100%",
          height: "100%",
        }}
      >
        <TextField
          multiline
          rows={6}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          inputRef={textareaRef}
          variant="outlined"
          size="small"
          sx={{
            width: "100%",
            "& .MuiInputBase-root": {
              fontSize: "8px",
              fontFamily: "monospace",
              padding: "4px",
            },
            "& .MuiOutlinedInput-input": {
              padding: "4px",
            },
          }}
        />

        {/* Save/Cancel buttons overlay */}
        <Box
          sx={{
            position: "absolute",
            top: 2,
            right: 2,
            display: "flex",
            gap: 0.5,
            backgroundColor: "rgba(255, 255, 255, 0.9)",
            borderRadius: 1,
          }}
        >
          <IconButton
            size="small"
            onClick={handleSave}
            disabled={saving}
            sx={{ p: 0.5, minWidth: 20, height: 20 }}
          >
            {saving ? (
              <CircularProgress size={12} />
            ) : (
              <SaveIcon sx={{ fontSize: 12 }} />
            )}
          </IconButton>
          <IconButton
            size="small"
            onClick={handleCancel}
            disabled={saving}
            sx={{ p: 0.5, minWidth: 20, height: 20 }}
          >
            <CancelIcon sx={{ fontSize: 12 }} />
          </IconButton>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      onClick={handleEditStart}
      sx={{
        width: "100%",
        height: "100%",
        cursor: "pointer",
        "&:hover": {
          backgroundColor: "rgba(0, 0, 0, 0.04)",
        },
      }}
    >
      <Typography
        variant="body2"
        sx={{
          fontSize: "8px",
          fontFamily: "monospace",
          whiteSpace: "pre-wrap",
          color: notes ? "text.primary" : "text.secondary",
          height: "100%",
          overflow: "auto",
          padding: "4px",
          lineHeight: 1.2,
        }}
      >
        {notes || "Click to add notes..."}
      </Typography>
    </Box>
  );
}
